import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../cabecalho/Cabecalho';
import Menu from '../menu/Menu';
import Rodape from '../rodape/Rodape';
import Titulo from '../titulo/Titulo';
import Snackbar from '../common/Snackbar';
import ChamadosService from '../../service/chamados/ChamadosService';
import ManutencaoCarrinhosService from '../../service/manutencaoCarrinhos/ManutencaoCarrinhosService';

const STATUS_CONFIG = {
  Aberto: { color: '#1a73e8', bg: '#e8f0fe', dot: '#1a73e8' },
  'Em andamento': { color: '#f59e0b', bg: '#fef9c3', dot: '#f59e0b' },
  Concluído: { color: '#16a34a', bg: '#dcfce7', dot: '#16a34a' },
  Cancelado: { color: '#dc2626', bg: '#fee2e2', dot: '#dc2626' },
};

const AVATAR_COLORS = ['#008080', '#1a73e8', '#f59e0b', '#16a34a', '#7c3aed', '#0ea5e9'];

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
}

function colorFromId(id) {
  if (!id) return '#008080';
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function formatarDataHora(valor) {
  if (!valor) return '-';
  const data = new Date(valor);
  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function mapearChamadoDetalhe(chamado) {
  return {
    ...chamado,
    mensagens: Array.isArray(chamado?.mensagens)
      ? chamado.mensagens.map((mensagem) => ({
          ...mensagem,
          autorNome: mensagem?.autor?.nome || 'Usuário',
          autorId: mensagem?.autor?._id || null,
          autorAvatarBg: colorFromId(mensagem?.autor?._id || mensagem?._id || ''),
        }))
      : [],
  };
}

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  overflow: hidden;
  box-sizing: border-box;
  padding-top: clamp(0.375rem, 0.8vh, 0.75rem);
`;

const MainContentWrapper = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  min-height: 0;
  margin-top: 85px;
  margin-bottom: 50px;
`;

const Container = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-left: 255px;
  padding: clamp(1rem, 2vh, 1.5rem) clamp(1rem, 2.5vw, 2rem);
  box-sizing: border-box;

  @media (max-width: 1200px) {
    margin-left: 60px;
    padding-left: 20px;
  }

  @media (max-width: 767.98px) {
    margin-left: 0;
    padding: clamp(0.75rem, 3vw, 1.25rem);
  }
`;

const Content = styled.main`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  margin: 0 auto;
  font-family: 'Roboto', sans-serif;
  box-sizing: border-box;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const BackButton = styled.button`
  border: 1.5px solid #b2d8d8;
  background: white;
  color: #008080;
  border-radius: 8px;
  padding: 9px 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover { background: #f3fbfb; }
`;

const HeroCard = styled.section`
  background: linear-gradient(135deg, #ffffff 0%, #f4fbfb 100%);
  border: 1px solid #d9eeee;
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(0, 128, 128, 0.08);
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const HeroHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const HeroTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const NumeroChamado = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #008080;
  letter-spacing: 0.4px;
  text-transform: uppercase;
`;

const TituloChamado = styled.h2`
  margin: 0;
  color: #163232;
  font-size: clamp(1.3rem, 2vw, 1.8rem);
  font-weight: 700;
  line-height: 1.2;
`;

const DescricaoChamado = styled.p`
  margin: 0;
  color: #4a4a4a;
  font-size: 14px;
  line-height: 1.6;
  max-width: 900px;
`;

const ChipRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const CodeChip = styled.span`
  display: inline-flex;
  align-items: center;
  background: #e0f2f1;
  color: #005f5f;
  border-radius: 999px;
  padding: 6px 12px;
  font-family: 'Roboto Mono', 'Courier New', monospace;
  font-size: 12px;
  font-weight: 700;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => STATUS_CONFIG[p.$status]?.color || '#555'};
  background: ${(p) => STATUS_CONFIG[p.$status]?.bg || '#f0f0f0'};
`;

const StatusDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${(p) => STATUS_CONFIG[p.$status]?.dot || '#aaa'};
`;

const MainGrid = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(300px, 0.95fr);
  gap: 18px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.section`
  background: white;
  border-radius: 18px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  border: 1px solid #eef2f2;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  padding: 18px 20px 14px;
  border-bottom: 1px solid #eef2f2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  h3 {
    margin: 0;
    font-size: 16px;
    color: #163232;
  }

  span {
    color: #8e8e93;
    font-size: 12px;
  }
`;

const MessagesList = styled.div`
  max-height: 620px;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background:
    radial-gradient(circle at top right, rgba(0, 128, 128, 0.05), transparent 32%),
    linear-gradient(180deg, #fbfefe 0%, #ffffff 100%);
`;

const EmptyState = styled.div`
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
  padding: 32px 18px;
`;

const MessageRow = styled.div`
  display: flex;
  justify-content: ${(p) => (p.$mine ? 'flex-end' : 'flex-start')};
`;

const MessageBubble = styled.div`
  max-width: min(82%, 560px);
  background: ${(p) => (p.$mine ? '#008080' : '#f3f7f7')};
  color: ${(p) => (p.$mine ? 'white' : '#243232')};
  border-radius: 18px;
  padding: 14px 16px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.07);
`;

const MessageMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

const MessageAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(p) => p.$bg || '#008080'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
`;

const MessageAuthor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-size: 13px;
    color: inherit;
  }

  span {
    font-size: 11px;
    opacity: 0.8;
  }
`;

const MessageText = styled.p`
  margin: 0;
  white-space: pre-wrap;
  line-height: 1.6;
  font-size: 14px;
`;

const Composer = styled.form`
  padding: 16px 20px 20px;
  border-top: 1px solid #eef2f2;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Textarea = styled.textarea`
  width: 100%;
  border: 1.5px solid #d0d7d7;
  border-radius: 14px;
  padding: 14px 16px;
  min-height: 110px;
  resize: vertical;
  box-sizing: border-box;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  outline: none;

  &:focus { border-color: #008080; }
`;

const ComposerActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const Hint = styled.span`
  font-size: 12px;
  color: #8e8e93;
`;

const PrimaryButton = styled.button`
  border: none;
  border-radius: 10px;
  padding: 10px 18px;
  background: #008080;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover { background: #006f6f; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const AsideBody = styled.div`
  padding: 18px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 700;
  color: #008080;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Select = styled.select`
  border: 1.5px solid #d0d7d7;
  border-radius: 10px;
  padding: 10px 12px;
  background: white;
  font-size: 14px;
  color: #263232;
  outline: none;

  &:focus { border-color: #008080; }
`;

const DataGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const DataCard = styled.div`
  border-radius: 14px;
  background: #f8fbfb;
  border: 1px solid #e4eeee;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  small {
    color: #8e8e93;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  strong {
    color: #203636;
    font-size: 14px;
    line-height: 1.4;
  }
`;

const PersonCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  background: #f8fbfb;
  border: 1px solid #e4eeee;
`;

const PersonInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-size: 14px;
    color: #203636;
  }

  span {
    font-size: 12px;
    color: #8e8e93;
  }
`;

const PageHint = styled.p`
  margin: 0;
  font-size: 13px;
  color: #8e8e93;
`;

const ChamadoDetalhe = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const usuarioLogadoId = useMemo(() => localStorage.getItem('idUsuario') || '', []);

  const [chamado, setChamado] = useState(null);
  const [responsaveis, setResponsaveis] = useState([]);
  const [mensagem, setMensagem] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [salvandoCampo, setSalvandoCampo] = useState(false);
  const [enviandoMensagem, setEnviandoMensagem] = useState(false);
  const [snackbar, setSnackbar] = useState({ aberto: false, mensagem: '', tipo: 'success' });

  const mostrarSnackbar = (mensagemTexto, tipo = 'success') => {
    setSnackbar({ aberto: true, mensagem: mensagemTexto, tipo });
  };

  const carregarDetalhe = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const [detalhe, listaUsuarios] = await Promise.all([
        ChamadosService.buscarPorId(id),
        ManutencaoCarrinhosService.listarResponsaveis(),
      ]);
      setChamado(mapearChamadoDetalhe(detalhe));
      const listaResponsaveis = Array.isArray(listaUsuarios)
        ? listaUsuarios
            .map((item) => item?.usuario)
            .filter((usuario) => usuario?._id && usuario?.nome)
        : [];
      if (detalhe?.atribuidoA?._id && !listaResponsaveis.some((usuario) => String(usuario._id) === String(detalhe.atribuidoA._id))) {
        listaResponsaveis.unshift(detalhe.atribuidoA);
      }
      setResponsaveis(listaResponsaveis);
    } catch (error) {
      setErro(error?.response?.data?.erro || error?.message || 'Não foi possível carregar o chamado.');
    } finally {
      setCarregando(false);
    }
  }, [id]);

  useEffect(() => {
    carregarDetalhe();
  }, [carregarDetalhe]);

  const handleAtualizarStatus = async (event) => {
    const novoStatus = event.target.value;
    if (!novoStatus || !chamado) return;
    setSalvandoCampo(true);
    try {
      const atualizado = await ChamadosService.atualizarStatus(chamado._id, novoStatus);
      setChamado(mapearChamadoDetalhe(atualizado));
      mostrarSnackbar(`Status atualizado para “${novoStatus}”.`);
    } catch (error) {
      mostrarSnackbar('Erro ao atualizar status.', 'error');
    } finally {
      setSalvandoCampo(false);
    }
  };

  const handleAtualizarResponsavel = async (event) => {
    const atribuidoAId = event.target.value || null;
    if (!chamado) return;
    setSalvandoCampo(true);
    try {
      const atualizado = await ChamadosService.editar(chamado._id, { atribuidoAId });
      setChamado(mapearChamadoDetalhe(atualizado));
      mostrarSnackbar(atribuidoAId ? 'Responsável atualizado com sucesso.' : 'Chamado desatribuído com sucesso.');
    } catch (error) {
      mostrarSnackbar('Erro ao atualizar responsável.', 'error');
    } finally {
      setSalvandoCampo(false);
    }
  };

  const handleEnviarMensagem = async (event) => {
    event.preventDefault();
    if (!mensagem.trim() || !chamado) return;
    setEnviandoMensagem(true);
    try {
      const atualizado = await ChamadosService.adicionarMensagem(chamado._id, mensagem.trim());
      setChamado(mapearChamadoDetalhe(atualizado));
      setMensagem('');
      mostrarSnackbar('Mensagem enviada com sucesso.');
    } catch (error) {
      mostrarSnackbar('Erro ao enviar mensagem.', 'error');
    } finally {
      setEnviandoMensagem(false);
    }
  };

  return (
    <>
      <PageWrapper>
        <Header />
        <MainContentWrapper>
          <Menu />
          <Container>
            <Content>
              <TopBar>
                <div>
                  <Titulo>Chamado</Titulo>
                  <PageHint>Acompanhe o histórico, troque mensagens e administre o atendimento</PageHint>
                </div>
                <BackButton onClick={() => navigate('/chamados')}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 12H5" />
                    <path d="M12 19l-7-7 7-7" />
                  </svg>
                  Voltar para chamados
                </BackButton>
              </TopBar>

              {carregando ? (
                <Panel>
                  <EmptyState>Carregando chamado...</EmptyState>
                </Panel>
              ) : erro ? (
                <Panel>
                  <EmptyState>{erro}</EmptyState>
                </Panel>
              ) : chamado ? (
                <>
                  <HeroCard>
                    <HeroHeader>
                      <HeroTitleBlock>
                        <NumeroChamado>{chamado.numero}</NumeroChamado>
                        <TituloChamado>{chamado.titulo}</TituloChamado>
                        <DescricaoChamado>{chamado.descricao}</DescricaoChamado>
                      </HeroTitleBlock>
                      <ChipRow>
                        {chamado.codigo ? <CodeChip>{chamado.codigo}</CodeChip> : null}
                        <StatusPill $status={chamado.status}>
                          <StatusDot $status={chamado.status} />
                          {chamado.status}
                        </StatusPill>
                      </ChipRow>
                    </HeroHeader>
                    <DataGrid>
                      <DataCard>
                        <small>Categoria</small>
                        <strong>{chamado.categoria || 'Manutenção de carrinhos'}</strong>
                      </DataCard>
                      <DataCard>
                        <small>Última atualização</small>
                        <strong>{formatarDataHora(chamado.updatedAt)}</strong>
                      </DataCard>
                    </DataGrid>
                  </HeroCard>

                  <MainGrid>
                    <Panel>
                      <PanelHeader>
                        <h3>Conversa do chamado</h3>
                        <span>{chamado.mensagens?.length || 0} mensagem(ns)</span>
                      </PanelHeader>

                      <MessagesList>
                        {chamado.mensagens?.length ? chamado.mensagens.map((item) => {
                          const minhaMensagem = String(item.autorId || '') === String(usuarioLogadoId || '');
                          return (
                            <MessageRow key={item._id || `${item.autorId}-${item.createdAt}`} $mine={minhaMensagem}>
                              <MessageBubble $mine={minhaMensagem}>
                                <MessageMeta>
                                  <MessageAvatar $bg={item.autorAvatarBg}>{getInitials(item.autorNome)}</MessageAvatar>
                                  <MessageAuthor>
                                    <strong>{item.autorNome}</strong>
                                    <span>{formatarDataHora(item.createdAt)}</span>
                                  </MessageAuthor>
                                </MessageMeta>
                                <MessageText>{item.texto}</MessageText>
                              </MessageBubble>
                            </MessageRow>
                          );
                        }) : <EmptyState>Nenhuma mensagem ainda. Use o campo abaixo para iniciar a conversa.</EmptyState>}
                      </MessagesList>

                      <Composer onSubmit={handleEnviarMensagem}>
                        <Textarea
                          value={mensagem}
                          onChange={(event) => setMensagem(event.target.value)}
                          placeholder="Escreva uma atualização, orientação ou resposta sobre este chamado"
                        />
                        <ComposerActions>
                          <Hint>As mensagens ficam registradas no histórico do chamado.</Hint>
                          <PrimaryButton type="submit" disabled={enviandoMensagem || !mensagem.trim()}>
                            {enviandoMensagem ? 'Enviando...' : 'Enviar mensagem'}
                          </PrimaryButton>
                        </ComposerActions>
                      </Composer>
                    </Panel>

                    <Panel>
                      <PanelHeader>
                        <h3>Dados do atendimento</h3>
                        <span>{salvandoCampo ? 'Salvando...' : ''}</span>
                      </PanelHeader>
                      <AsideBody>
                        <FieldBlock>
                          <Label>Status</Label>
                          <Select value={chamado.status || 'Aberto'} onChange={handleAtualizarStatus} disabled={salvandoCampo}>
                            <option value="Aberto">Aberto</option>
                            <option value="Em andamento">Em andamento</option>
                            <option value="Concluído">Concluído</option>
                            <option value="Cancelado">Cancelado</option>
                          </Select>
                        </FieldBlock>

                        <FieldBlock>
                          <Label>Atribuir responsável</Label>
                          <Select value={chamado.atribuidoA?._id || ''} onChange={handleAtualizarResponsavel} disabled={salvandoCampo}>
                            <option value="">Sem responsável</option>
                            {responsaveis.map((usuario) => (
                              <option key={usuario._id} value={usuario._id}>{usuario.nome}</option>
                            ))}
                          </Select>
                        </FieldBlock>

                        <FieldBlock>
                          <Label>Participantes</Label>
                          <PersonCard>
                            <MessageAvatar $bg={colorFromId(chamado.criadoPor?._id || '')}>{getInitials(chamado.criadoPor?.nome)}</MessageAvatar>
                            <PersonInfo>
                              <strong>{chamado.criadoPor?.nome || 'Usuário'}</strong>
                              <span>Criou o chamado</span>
                            </PersonInfo>
                          </PersonCard>
                          <PersonCard>
                            <MessageAvatar $bg={colorFromId(chamado.atribuidoA?._id || chamado._id || '')}>{getInitials(chamado.atribuidoA?.nome || 'SR')}</MessageAvatar>
                            <PersonInfo>
                              <strong>{chamado.atribuidoA?.nome || 'Sem responsável definido'}</strong>
                              <span>Responsável atual</span>
                            </PersonInfo>
                          </PersonCard>
                        </FieldBlock>

                        <DataGrid>
                          <DataCard>
                            <small>Criado em</small>
                            <strong>{formatarDataHora(chamado.createdAt)}</strong>
                          </DataCard>
                          <DataCard>
                            <small>Código do item</small>
                            <strong>{chamado.codigo || 'Não informado'}</strong>
                          </DataCard>
                          <DataCard>
                            <small>Número do chamado</small>
                            <strong>{chamado.numero}</strong>
                          </DataCard>
                          <DataCard>
                            <small>Categoria</small>
                            <strong>{chamado.categoria || 'Manutenção de carrinhos'}</strong>
                          </DataCard>
                        </DataGrid>
                      </AsideBody>
                    </Panel>
                  </MainGrid>
                </>
              ) : null}
            </Content>
          </Container>
        </MainContentWrapper>
        <Rodape />
      </PageWrapper>

      {snackbar.aberto && (
        <Snackbar
          mensagem={snackbar.mensagem}
          tipo={snackbar.tipo}
          onClose={() => setSnackbar((atual) => ({ ...atual, aberto: false }))}
        />
      )}
    </>
  );
};

export default ChamadoDetalhe;